import camelCase from 'lodash/camelCase';
import extend from 'extend';
import uniqid from 'uniqid';

const pubsub = PubSub();
let App = null;
let appNamespace = null;

export function ParameterBox(initialParameters) {
    let parameters = typeof initialParameters === 'object' ? initialParameters : {};

    function get(key, defaultValue = null) {
        return parameters[key] !== undefined ? parameters[key] : defaultValue;
    }

    function set(key, value) {
        parameters[key] = value;
    }

    function append(key, value) {
        if (Array.isArray(parameters[key])) {
            parameters[key].push(value);
        } else {
            parameters[key] += value;
        }
    }

    function all() {
        return parameters;
    }

    function keys() {
        return Object.keys(parameters);
    }

    function replace(newParameters) {
        parameters = newParameters;
    }

    function add(newParameters) {
        extend(true, parameters, newParameters);
    }

    function has(key) {
        return parameters[key] !== undefined;
    }

    function remove(key) {
        delete parameters[key];
    }

    return {
        get: get,
        set: set,
        append,
        all,
        keys,
        replace,
        add,
        has,
        remove
    };
}

/* zmodyfikowana wersja: https://jsmanifest.com/the-publish-subscribe-pattern-in-javascript/ */
export function PubSub() {
    const subscribers = {};

    function publish(eventName, data) {
        if (!Array.isArray(subscribers[eventName])) {
            return;
        }

        if (!data) {
            data = [];
        }

        subscribers[eventName].forEach((callback) => {
            callback(...data);
        });
    }

    function subscribe(eventName, callback) {
        if (!Array.isArray(subscribers[eventName])) {
            subscribers[eventName] = [];
        }
        subscribers[eventName].push(callback);
    }

    return {
        publish,
        subscribe
    };
}

export function getId(prefix, suffix) {
    let newId = (prefix ? prefix + '-' : '') + uniqid() + (suffix ? '-' + suffix : '');

    if (App.generatedIds.indexOf(newId) > -1) {
        newId = getId(prefix, suffix);
    } else {
        App.generatedIds.push(newId);
    }

    return newId;
}

export function createView(name, viewDef) {
    const view = {
        $el: null
    };

    view.init = function($view) {
        if (!view.$el) {
            const finishInitiation = function() {
                if (typeof viewDef.onInit === 'function') {
                    viewDef.onInit.call(view, $view);
                }

                Object.defineProperty(view, "$el", {writable: false, configurable: false});
                Object.seal(view);
            }
            view.$el = $view;

            /* ustawiamy konfigurację */
            view.config = {};

            if (viewDef.config) {
                extend(true, view.config, viewDef.config);
            }
            if ($view.dataset.view) {
                extend(true, view.config, JSON.parse($view.dataset.view));
            }

            /* jeśli podano moduły to ładujemy wszystkie i dodajemy je jako własności obiektu view */
            if (viewDef.modules) {
                const modulesImports = [];

                for (let i = 0, len = viewDef.modules.length; i < len; i++) {
                    const moduleFilename = viewDef.modules[i];
                    const cased = camelCase(moduleFilename);
                    const moduleName = cased.charAt(0).toUpperCase() + cased.slice(1);

                    modulesImports.push(
                        import(MODULES_PATH + moduleFilename).then(module => {
                            view[moduleName] = module[moduleName];
                        })
                    );
                }

                Promise.all(modulesImports).then(finishInitiation);
            } else {
                finishInitiation();
            }
        }

        return view;
    }

    window[appNamespace].views[name] = view;

    return view;
}

export function callViews() {
    const views = ICUBE_CONFIG.views || {};
    const viewsObjectKeys = Object.keys(views);

    if (viewsObjectKeys.length) {
        const selector = viewsObjectKeys.filter((id) => !views[id].static).map((id) => '#' + id).join(',');

        if (selector) {
            const $viewsElements = document.querySelectorAll(selector);

            if ($viewsElements.length) {
                for (let i = 0, len = $viewsElements.length; i < len; i++) {
                    const $view = $viewsElements[i];
                    const name = views[$view.id].name;

                    import(VIEWS_PATH + name).then(module => {
                        if (typeof module.default === 'function') { /* kompatybilność wsteczna */
                            module.default.call($view);
                        } else {
                            createView(name, module.default).init($view);
                        }
                    });
                }
            }
        }
    }
}

export function createAppState(name, params) {
    if (!App) {
        window[name] = extend(true, window[name] || {}, {
            appConfig: {},
            data: ParameterBox(),
            mixins: {},
            modules: {},
            views: {},
            generatedIds: [],
            ...params
        });
        App = window[name];
        appNamespace = name;
    }
}

/*
    TODO: (rozważyć) optymalizacja -> nie tworzyć nowych MutationObserver.
    Więcej na ten temat: https://stackoverflow.com/questions/31659567/performance-of-mutationobserver-to-detect-nodes-in-entire-dom
*/
export function trace(selector, callback, parent, searchInNode) {
    const elements = document.querySelectorAll(selector);
    const observeOptions = {childList: true, subtree: true};

    if (!parent) {
        parent = document.querySelectorAll('body');
    } else if (typeof parent === 'string') {
        parent = document.querySelectorAll(parent);
    }

    pubsub.subscribe('trace-' + selector, callback);

    const observerCallback = function(mutationsList, observer) {
        for (let i = 0, iLen = mutationsList.length; i < iLen; i++) {
            const mutation = mutationsList[i];

            if (mutation.addedNodes.length) {
                for (let j = 0, jLen = mutation.addedNodes.length; j < jLen; j++) {
                    const newNode = mutation.addedNodes[j];

                    if (newNode.matches && newNode.matches(selector)) {
                        pubsub.publish('trace-' + selector, [newNode, true]);
                    } else if (searchInNode && newNode.querySelectorAll) {
                        const matchedNodes = newNode.querySelectorAll(selector);

                        if (matchedNodes.length) {
                            pubsub.publish('trace-' + selector, [matchedNodes, true]);
                        }
                    }
                }
            }
        }
    };

    for (let i = 0, len = parent.length; i < len; i++) {
        const observer = new MutationObserver(observerCallback);

        observer.observe(parent[i], observeOptions);
    }

    if (elements.length) {
        callback(elements, false);
    }
}

export function setAppConfig(config) {
    App.appConfig = config;
}

export function getAppConfig() {
    return App.appConfig;
}